import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Copy, EnvelopeSimple, LinkSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDateTime } from "../../lib/dateUtil";

const STATE_CLS = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  used: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  expired: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

export default function SupplierUpdateLinkDialog({ vendor, open, onOpenChange }) {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => API.get(`/suppliers/${vendor.id}/update-link/status`).then(r => setStatus(r.data)).catch(() => setStatus({ email_configured: false, email_on_file: vendor.email, tokens: [] }));

  useEffect(() => {
    if (!open || !vendor?.id) return;
    setLink(null);
    setStatus(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendor?.id]);

  const generate = async () => {
    setBusy("generate");
    try {
      const { data } = await API.post(`/suppliers/${vendor.id}/update-link`);
      setLink(data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create the link"); }
    finally { setBusy(""); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(link.link); toast.success("Update link copied"); }
    catch { window.prompt("Copy this update link", link.link); }
  };

  const email = async () => {
    setBusy("email");
    try {
      const { data } = await API.post(`/suppliers/${vendor.id}/update-link/email`);
      toast.success(`Update link emailed to ${data.email}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send the email"); }
    finally { setBusy(""); }
  };

  if (!vendor) return null;
  const emailOnFile = status?.email_on_file || vendor.email || "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="supplier-update-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2"><LinkSimple size={18} /> Supplier update link</DialogTitle>
          <DialogDescription>
            A personal, single-use link (valid {status?.expires_days || 7} days) that lets <span className="font-semibold text-foreground">{vendor.company_name}</span> review and
            update their own contact, company, coverage, document and payment details on the NECOS.FM website.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {link ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/15 dark:border-emerald-900/40 p-3 space-y-2" data-testid="update-link-result">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-300">Link ready</p>
              <div className="flex gap-2">
                <Input readOnly value={link.link} className="text-xs font-mono" onFocus={e => e.target.select()} data-testid="update-link-url" />
                <Button size="sm" onClick={copy} data-testid="update-link-copy-btn"><Copy size={14} className="mr-1" /> Copy</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Expires {fmtDateTime(link.expires_at)} · single use</p>
            </div>
          ) : (
            <Button onClick={generate} disabled={busy === "generate"} className="w-full" data-testid="update-link-generate-btn">
              <ArrowsClockwise size={14} className={`mr-1 ${busy === "generate" ? "animate-spin" : ""}`} /> Generate link
            </Button>
          )}

          {status?.email_configured && emailOnFile && (
            <Button variant="outline" onClick={email} disabled={busy === "email"} className="w-full" data-testid="update-link-email-btn">
              <EnvelopeSimple size={14} className="mr-1" /> Email link to supplier <span className="ml-1 text-muted-foreground font-normal truncate">({emailOnFile})</span>
            </Button>
          )}
          {status && !status.email_configured && (
            <p className="text-[11px] text-muted-foreground" data-testid="update-link-no-email-transport">Email isn't configured on this portal — copy the link and send it yourself.</p>
          )}
          {status && !emailOnFile && (
            <p className="text-[11px] text-amber-700" data-testid="update-link-no-email">No email address on file — copy the link and send it manually.</p>
          )}

          {status?.tokens?.length > 0 && (
            <div data-testid="update-link-recent">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">Recent links</p>
              <div className="space-y-1">
                {status.tokens.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] text-muted-foreground border border-border rounded px-2 py-1">
                    <span>{fmtDateTime(t.created_at)} · {t.source === "necosfm.com" ? "requested on website" : t.emailed_to ? `emailed to ${t.emailed_to}` : `by ${t.created_by || "staff"}`}</span>
                    <Badge className={`text-[9px] ${STATE_CLS[t.state] || STATE_CLS.used}`}>{t.state}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
